import React, { useEffect, useMemo, useState } from "react";
import { Breadcrumbs } from "../AdminLayout";
import { navApi } from "./api";
import { formatErr } from "../api";
import { toast, Toaster } from "sonner";
import { Plus, Trash2, GripVertical, Save } from "lucide-react";

const MENUS = ["primary", "footer", "utility", "doctor_portal"];

export default function NavigationManager() {
  const [menu, setMenu] = useState("primary");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ label: "", href: "", order: 1, open_in_new_tab: false });

  const reload = () => {
    navApi.list(menu).then(setItems).catch(e => toast.error(formatErr(e.response?.data?.detail)));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [menu]);

  const add = async () => {
    if (!form.label || !form.href) { toast.error("Label and href are required"); return; }
    setBusy(true);
    try {
      await navApi.create({ ...form, menu, order: Number(form.order || 0) });
      setForm({ label: "", href: "", order: (items.length || 0) + 1, open_in_new_tab: false });
      toast.success("Item added"); reload();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this navigation item?")) return;
    try { await navApi.remove(id); toast.success("Removed"); reload(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const updateOrder = async (id, order) => {
    try { await navApi.update(id, { order: Number(order) }); reload(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const toggleVisible = async (row) => {
    try { await navApi.update(row.id, { visible: !row.visible }); reload(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const inputCls = "border border-line px-3 py-2 text-sm";
  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="nav-manager">
      <Toaster position="top-right" richColors/>
      <Breadcrumbs items={[{ label: "Site" }, { label: "Navigation" }]}/>
      <div className="font-serif text-3xl text-navy mt-4 mb-6">Navigation Manager</div>

      <div className="flex items-center gap-2 border-b border-line mb-6">
        {MENUS.map(m => (
          <button key={m} data-testid={`nav-menu-${m}`} onClick={() => setMenu(m)}
                  className={`mono text-[10px] uppercase tracking-widest px-4 py-2 border-b-2 -mb-px ${menu === m ? "border-navy text-navy" : "border-transparent text-stone2 hover:text-navy"}`}>{m.replace(/_/g, " ")}</button>
        ))}
      </div>

      <div className="bg-white border border-line p-5 mb-5">
        <div className="mono text-[10px] uppercase tracking-widest text-prestige mb-3">Add new item to <b>{menu.replace(/_/g, " ")}</b></div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input data-testid="nav-input-label" placeholder="Label" value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} className={inputCls}/>
          <input data-testid="nav-input-href" placeholder="/path or https://…" value={form.href} onChange={e => setForm(f => ({ ...f, href: e.target.value }))} className={inputCls}/>
          <input data-testid="nav-input-order" type="number" placeholder="Order" value={form.order} onChange={e => setForm(f => ({ ...f, order: e.target.value }))} className={inputCls}/>
          <button data-testid="nav-add" onClick={add} disabled={busy} className="bg-navy hover:bg-navy-hover text-white text-sm px-4 py-2 inline-flex items-center gap-2 justify-center disabled:opacity-60"><Plus size={13}/> Add</button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-line p-8 text-center text-sm text-stone2">No items in this menu yet.</div>
      ) : (
        <ul className="space-y-2">
          {items.map(it => (
            <li key={it.id} data-testid={`nav-row-${it.id}`} className="bg-white border border-line px-4 py-3 flex items-center gap-3">
              <GripVertical size={14} className="text-stone2 shrink-0"/>
              <input type="number" defaultValue={it.order} onBlur={e => updateOrder(it.id, e.target.value)} className="w-16 border border-line px-2 py-1 text-xs" data-testid={`nav-order-${it.id}`}/>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-navy truncate">{it.label}</div>
                <div className="mono text-[10px] text-stone2 truncate">{it.href}</div>
              </div>
              <button data-testid={`nav-visible-${it.id}`} onClick={() => toggleVisible(it)} className={`mono text-[10px] uppercase tracking-widest px-2 py-1 ${it.visible ? "bg-[#3F6250]/10 text-[#3F6250]" : "bg-stone2/10 text-stone2"}`}>
                {it.visible ? "Visible" : "Hidden"}
              </button>
              <button data-testid={`nav-remove-${it.id}`} onClick={() => remove(it.id)} className="text-[#9F1239] hover:text-[#7B0F2C]"><Trash2 size={14}/></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
